/**
 * MasterPower ambient sensor logic: temperature, humidity and condensation risk.
 *
 * The relay reports air temperature + relative humidity from an HTU21 sensor.
 * Condensation forms when a surface cools to the dew point, so the real signal is
 * not "hot AND humid" but how close the current temperature is to the dew point
 * (the margin). We compute the dew point with the Magnus formula and grade the
 * margin: a small margin means condensation is imminent even at moderate humidity.
 *
 * We only have the HTU21 air reading (no surface probe), so the air temperature is
 * used as a conservative proxy for the coldest-surface temperature.
 */

import type { View } from '@/types';
import type { StatusType } from './StatusDashboardIcon';

export type SensorLevel = 'ok' | 'warning' | 'critical';

/** Map a sensor level to the dashboard icon status color. */
export const SENSOR_LEVEL_STATUS: Record<SensorLevel, StatusType> = {
  ok: 'success',
  warning: 'warning',
  critical: 'error',
};

// ── Thresholds ──────────────────────────────────────────────────────
// Temperature (°C, ambient): <40 OK, 40–50 warning, >50 critical
const TEMP_WARNING_C = 40;
const TEMP_CRITICAL_C = 50;
// Relative humidity (%): <70 OK, 70–85 warning, >85 critical
const HUMIDITY_WARNING_PCT = 70;
const HUMIDITY_CRITICAL_PCT = 85;
// Condensation margin (T − dewPoint, °C): >5 OK, 2–5 warning, <2 critical
const CONDENSATION_WARNING_MARGIN_C = 5;
const CONDENSATION_CRITICAL_MARGIN_C = 2;

/**
 * Dew point via the Magnus-Tetens approximation.
 * Valid for 0–60 °C / 1–100 % RH, accuracy ±0.4 °C.
 */
export function computeDewPoint(tempC: number, humidityPct: number): number {
  const a = 17.27;
  const b = 237.7;
  // Guard against RH <= 0 (log of 0) — clamp to a tiny positive value.
  const rh = Math.max(humidityPct, 0.1) / 100;
  const gamma = (a * tempC) / (b + tempC) + Math.log(rh);
  return (b * gamma) / (a - gamma);
}

export interface CondensationRisk {
  level: SensorLevel;
  dewPoint: number; // °C
  margin: number;   // °C, temperature − dewPoint
}

/** Grade condensation risk from the air temperature + humidity. */
export function getCondensationRisk(tempC: number, humidityPct: number): CondensationRisk {
  const dewPoint = computeDewPoint(tempC, humidityPct);
  const margin = tempC - dewPoint;
  let level: SensorLevel = 'ok';
  if (margin < CONDENSATION_CRITICAL_MARGIN_C) level = 'critical';
  else if (margin < CONDENSATION_WARNING_MARGIN_C) level = 'warning';
  return { level, dewPoint, margin };
}

export function getTemperatureLevel(tempC: number): SensorLevel {
  if (tempC >= TEMP_CRITICAL_C) return 'critical';
  if (tempC >= TEMP_WARNING_C) return 'warning';
  return 'ok';
}

/**
 * Humidity level is the worst of the plain humidity threshold and the
 * condensation risk — high RH only matters insofar as it brings the dew point
 * close to the current temperature.
 */
export function getHumidityLevel(tempC: number, humidityPct: number): SensorLevel {
  const plain: SensorLevel =
    humidityPct >= HUMIDITY_CRITICAL_PCT ? 'critical'
    : humidityPct >= HUMIDITY_WARNING_PCT ? 'warning'
    : 'ok';
  return worstLevel(plain, getCondensationRisk(tempC, humidityPct).level);
}

function worstLevel(a: SensorLevel, b: SensorLevel): SensorLevel {
  const rank: Record<SensorLevel, number> = { ok: 0, warning: 1, critical: 2 };
  return rank[a] >= rank[b] ? a : b;
}

export interface MasterPowerSensorData {
  temperature: number;
  humidity: number;
  voltage: number;
  tempLevel: SensorLevel;
  humidityLevel: SensorLevel;
  condensation: CondensationRisk;
}

/**
 * Resolve the live sensor data only when the relay is connected and has reported
 * sensors. Returns null when no valid reading is available (relay offline / no
 * sensor data yet).
 */
export function getMasterPowerSensors(mp: View['masterPower']): MasterPowerSensorData | null {
  if (!mp?.connected || !mp.sensors) return null;
  const { temperature, humidity, voltage } = mp.sensors;
  // Guard against placeholder/invalid readings (e.g. 0/0 from older firmware).
  if (temperature <= 0 && humidity <= 0) return null;
  return {
    temperature,
    humidity,
    voltage,
    tempLevel: getTemperatureLevel(temperature),
    humidityLevel: getHumidityLevel(temperature, humidity),
    condensation: getCondensationRisk(temperature, humidity),
  };
}

/** Human-readable condensation label (semantic level only). */
export function condensationLabel(risk: CondensationRisk): string {
  if (risk.level === 'critical') return 'Condensation imminent';
  if (risk.level === 'warning') return 'Condensation possible';
  return 'Condensation risk low';
}

/** Technical detail line (dew point + margin). */
export function condensationDetail(risk: CondensationRisk): string {
  const dew = risk.dewPoint.toFixed(1);
  const margin = risk.margin.toFixed(1);
  if (risk.level === 'critical') {
    return `Dew point ${dew}°C, only ${margin}°C below current temperature. Moisture may be forming on cold surfaces.`;
  }
  if (risk.level === 'warning') {
    return `Dew point ${dew}°C, ${margin}°C below current temperature. Monitor if the temperature drops.`;
  }
  return `Dew point ${dew}°C, ${margin}°C margin.`;
}
